/**
 * Det som er delt med meg (sak 232, T4) — `GET /v1/me/shares` og systemlinjene i delingens tråd.
 * Grensesnittet er fryst i `docs/qa/eksterne-deling/PLAN.md` §1.5.
 *
 * Lista er MENNESKETS, på tvers av organisasjonene som har delt noe med hen, som konsertene og
 * oppgavene gitt meg. Den bærer også de avsluttede som har en tråd: kortet i tråden skal si
 * «Avsluttet» og ikke lenger kunne åpnes, framfor å forsvinne.
 */
import { z } from 'zod';

import { shareObjectTypeSchema, sharePermissionSchema } from './shares.js';

export const myShareDtoSchema = z
  .object({
    grantId: z.uuid(),
    organizationId: z.uuid(),
    organizationName: z.string(),
    objectType: shareObjectTypeSchema,
    objectId: z.uuid(),
    title: z.string(),
    startsAt: z.iso.datetime().nullable(),
    endsAt: z.iso.datetime().nullable(),
    permission: sharePermissionSchema,
    expiresAt: z.iso.datetime().nullable(),
    /** Når delingen ble trukket tilbake eller utløp. Null mens den lever. */
    endedAt: z.iso.datetime().nullable(),
    festivalId: z.uuid().nullable(),
    editionId: z.uuid().nullable(),
    /** Delingens tråd i Innboks. Null før den har nådd innboksen. */
    threadId: z.uuid().nullable(),
    /** «Legg i kalender» er trykket (P5). */
    inCalendar: z.boolean(),
    /** 260.10: delingen venter på Godta eller Avslå, og kan ikke åpnes før den er godtatt. */
    awaitingAnswer: z.boolean().optional(),
  })
  .meta({ id: 'MyShare' });
export type MyShareDto = z.infer<typeof myShareDtoSchema>;

export const myShareListSchema = z
  .object({ items: z.array(myShareDtoSchema) })
  .meta({ id: 'MyShareList' });
export type MyShareList = z.infer<typeof myShareListSchema>;

/**
 * «Legg i kalender» på kortet i tråden. Ruta eies av P5 (kalenderen); kortet kaller den.
 * `POST` legger delingen i kalenderen og svarer med kortet (`MyShareDto`).
 */
export function myShareCalendarPath(grantId: string): string {
  return `/me/shares/${grantId}/calendar`;
}

/**
 * SYSTEMLINJENE (`messages.body_format = 'system'`, 0462). Kroppen er JSON — nøkkelen og
 * parametrene — og nettleseren oversetter. Basen skriver aldri en setning.
 */
export const SHARE_LINE_KEYS = [
  'inbox.share.created',
  /* F10: flere objekter delt på én gang — én linje for alle. */
  'inbox.share.createdMany',
  'inbox.share.updated',
  'inbox.share.ended',
  /* F6: objektet ble slettet, og delingen avsluttet med det. */
  'inbox.share.deleted',
  'inbox.share.changed.time',
  'inbox.share.changed.place',
  'inbox.share.changed.openingHours',
  /* 260.10: mottakeren godtok eller avslo delingen. */
  'inbox.share.accepted',
  'inbox.share.declined',
  /* 1106 (§2.3): bookingens chat er åpnet for reisefølget — av hvem, fra hvilken organisasjon. */
  'inbox.chat.opened',
] as const;
export type ShareLineKey = (typeof SHARE_LINE_KEYS)[number];

export const systemLineSchema = z.object({
  key: z.enum(SHARE_LINE_KEYS),
  params: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
});
export type SystemLine = z.infer<typeof systemLineSchema>;

/** En systemlinje, eller null når kroppen ikke er en linje denne klienten kjenner. */
export function parseSystemLine(body: string): SystemLine | null {
  try {
    const parsed = systemLineSchema.safeParse(JSON.parse(body));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
